import React, { useState, useEffect, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { productGroups } from "../../mock";
import { useLanguage } from "../../contexts/LanguageContext";
import { ChevronRight, Search, Package } from "lucide-react";
import { t } from "../../i18n";
import CategorySidebar from "../layout/CategorySidebar";
import { getProductName, translateSubcategoryName, translateProductName } from "../../utils/translator";

const SearchPage = () => {
    const { lang } = useLanguage();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const searchTerm = queryParams.get("q") || "";

    const [searchIndex, setSearchIndex] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        fetch("/data/search_index.json")
            .then((res) => {
                if (!res.ok) {
                    throw new Error("Failed to load search index");
                }
                return res.json();
            })
            .then((data) => {
                setSearchIndex(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error loading search index:", err);
                setSearchIndex([]);
                setLoading(false);
            });
    }, []);

    const normalize = (str) => {
        if (!str) return "";
        return str.toString()
            .replace(/İ/g, 'i')
            .replace(/I/g, 'i')
            .replace(/ı/g, 'i')
            .toLowerCase()
            .trim()
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/[^a-z0-9]/g, '');
    };

    const normalizedSearchTerm = normalize(searchTerm);

    const filteredProducts = useMemo(() => {
        if (!normalizedSearchTerm || searchIndex.length === 0) return [];

        const results = [];
        searchIndex.forEach(product => {
            const nameTr = normalize(product.name_tr);
            const nameEn = normalize(translateProductName(product.name_en || product.name_tr));
            const series = normalize(translateProductName(product.series));
            const subcategory = normalize(translateProductName(product.subcategory));

            if (
                nameTr.includes(normalizedSearchTerm) ||
                nameEn.includes(normalizedSearchTerm) ||
                series.includes(normalizedSearchTerm) ||
                subcategory.includes(normalizedSearchTerm) ||
                String(product.id).includes(normalizedSearchTerm)
            ) {
                results.push(product);
            }
        });
        return results;
    }, [normalizedSearchTerm, searchIndex]);

    if (loading) {
        return (
            <div className="pt-[140px] bg-[#f5f5f5] min-h-screen flex items-center justify-center">
                <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    <p className="text-gray-500 text-sm tracking-widest uppercase font-bold animate-pulse">
                        {lang === "tr" ? "Arama Dizini Yükleniyor..." : "Loading Search Index..."}
                    </p>
                </div>
            </div>
        );
    }

    const breadcrumbs = [
        { title: t(lang, "nav.home"), path: "/" },
        { title: t(lang, "search.results"), path: null }
    ];

    return (
        <div className="pt-[140px] bg-[#f5f5f5] min-h-screen">
            <div className="bg-white border-b border-black/5 py-4 mb-8">
                <div className="mx-auto max-w-7xl px-4 flex items-center space-x-2 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                    {breadcrumbs.map((bc, index) => (
                        <React.Fragment key={index}>
                            {bc.path ? (
                                <Link to={bc.path} className="hover:text-primary transition-colors">
                                    {bc.title}
                                </Link>
                            ) : (
                                <span className="text-primary">{bc.title}</span>
                            )}
                            {index < breadcrumbs.length - 1 && (
                                <ChevronRight size={12} className="text-gray-300" />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-4 pb-12">
                <div className="flex flex-col lg:flex-row gap-10">
                    <CategorySidebar />
                    <div className="flex-grow">
                        <div className="flex items-center justify-between mb-8 pb-3 border-b-2 border-primary/10">
                            <h1 className="text-xl font-bold tracking-widest text-gray-900 uppercase">
                                {t(lang, "search.results")}: "{searchTerm}"
                            </h1>
                            <div className="bg-white px-4 py-1.5 rounded-full shadow-sm border border-gray-100 text-[10px] font-bold text-gray-400 tracking-widest uppercase">
                                {filteredProducts.length} {t(lang, "search.itemsFound")}
                            </div>
                        </div>

                        {filteredProducts.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredProducts.map((product) => (
                                    <Link
                                        key={`${product.categorySlug}-${product.id}`}
                                        to={`/products/${product.categorySlug}/${product.id}`}
                                        className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden border border-gray-100 flex flex-col h-full"
                                    >
                                        <div className="h-56 bg-white flex items-center justify-center p-6 group-hover:bg-primary/5 transition-colors duration-500 relative">
                                            <img
                                                src={product.imageUrl}
                                                alt={getProductName(product, lang)}
                                                className="max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-110"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "https://via.placeholder.com/400x400?text=Profinoks";
                                                }}
                                            />
                                            <div className="absolute top-4 right-4 bg-primary text-white text-[9px] font-bold px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                #{product.id}
                                            </div>
                                        </div>
                                        <div className="p-6 bg-white border-t border-gray-50 flex-grow flex flex-col justify-between">
                                            <div className="mb-4">
                                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
                                                    {translateSubcategoryName(product.subcategory || product.series, lang)}
                                                </span>
                                                <h3 className="text-[12px] font-bold text-gray-900 tracking-wide group-hover:text-primary transition-colors duration-300 line-clamp-2 min-h-[3rem]">
                                                    {getProductName(product, lang)}
                                                </h3>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center text-[10px] font-bold text-primary tracking-widest uppercase hover:underline decoration-2 underline-offset-4">
                                                    <span>{lang === "tr" ? "DETAYLAR" : "DETAILS"}</span>
                                                    <ChevronRight size={12} className="ml-1 transition-transform group-hover:translate-x-1" />
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-gray-100">
                                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Search className="text-gray-200 h-10 w-10" />
                                </div>
                                <h2 className="text-lg font-bold text-gray-400 uppercase tracking-widest mb-2">
                                    {t(lang, "search.noResults")} "{searchTerm}"
                                </h2>
                                <p className="text-gray-400 text-sm mb-8">
                                    {t(lang, "search.emptyState")}
                                </p>
                                <Link 
                                    to="/products" 
                                    className="inline-flex items-center px-6 py-3 bg-primary text-white text-[11px] font-bold uppercase tracking-widest rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                                >
                                    <Package size={16} className="mr-2" />
                                    {t(lang, "search.backToProducts")}
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchPage;
